import { useCallback, useLayoutEffect, useState } from 'react';

/**
 * The height of an element the browser sizes (a flex item that takes what is
 * left), for a chart that has to fill it. 0 while `enabled` is off. The first
 * reading is taken before the first paint, so the chart is never drawn at a
 * guess. `ref` is a callback ref: the element may only appear later (the
 * chart waits for its width).
 */
export function useContainerHeight<T extends HTMLElement = HTMLDivElement>(
    enabled: boolean
) {
    const [element, setElement] = useState<T | null>(null);
    const [height, setHeight] = useState(0);
    const ref = useCallback((el: T | null) => setElement(el), []);

    useLayoutEffect(() => {
        if (!enabled || !element) {
            setHeight(0);
            return undefined;
        }
        setHeight(element.getBoundingClientRect().height);
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(([entry]) =>
            setHeight(entry.contentRect.height)
        );
        observer.observe(element);
        return () => observer.disconnect();
    }, [enabled, element]);

    return { ref, height };
}
